import React, { useCallback, useMemo, useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import {
  Box,
  Button,
  Grid,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Typography,
  useTheme,
} from "@mui/material";

import {
  GRID_COLUMNS,
  MIN_COLUMN_SPAN,
  classicOrgDashboardLayout,
} from "../../../constants/org-dashboard";
import useOrgDashboardLayout from "../../../helpers/hooks/use-org-dashboard-layout";
import {
  addCard,
  addColumn,
  cardsInLayout,
  moveCardAcrossSplit,
  moveCardOutOfSplit,
  moveCardToColumn,
  moveCardWithinStack,
  moveColumn,
  removeCard,
  removeColumn,
  removeSplit,
  resizeColumn,
  resizeSplit,
  splitColumn,
} from "../../../helpers/org-dashboard-layout";
import {
  DashboardItem,
  DashboardLayout,
  DashboardPath,
  isSplitItem,
} from "../../../types/org-dashboard";
import {
  DashboardCardContext,
  cardCatalogue,
  cardCatalogueIds,
  findCard,
} from "./card-catalogue";
import ColumnMenu from "./column-menu";
import DashboardCardMenu from "./dashboard-card-menu";
import SplitMenu from "./split-menu";

export interface OrgDashboardProps extends DashboardCardContext {
  /** Height each card gets in the default view, matching the classic layout. */
  cardHeight?: number;
  /**
   * Optional controlled editing state. The organisation page drives this from a
   * gear in the summary strip, so viewing costs no vertical space at all; left
   * undefined the dashboard falls back to its own toggle.
   */
  editing?: boolean;
  onEditingChange?: (editing: boolean) => void;
}

/**
 * The organisation page's detail area (FG-8053).
 *
 * Cards sit in columns on a twelve-column grid; a column can be split in two.
 * Editing is opt-in and the default layout is the six-and-six pair the page has
 * always shown, so nothing moves until somebody chooses to move it.
 */
const OrgDashboard = ({
  cardHeight = 650,
  editing: editingProp,
  onEditingChange,
  ...cardContext
}: OrgDashboardProps) => {
  const theme = useTheme();
  const [editingState, setEditingState] = useState(false);
  const editing = editingProp ?? editingState;
  const setEditing = (next: boolean) => {
    setEditingState(next);
    onEditingChange?.(next);
  };
  const [addAnchor, setAddAnchor] = useState<HTMLElement | null>(null);
  const { layout, setLayout, resetLayout, isCustomised } =
    useOrgDashboardLayout(cardCatalogueIds);

  const used = useMemo(() => new Set(cardsInLayout(layout)), [layout]);
  const available = cardCatalogue.filter((card) => !used.has(card.id));

  const update = useCallback(
    (next: DashboardLayout) => setLayout(next),
    [setLayout],
  );

  // The full panes bring their own layout, so they keep the classic padded
  // Paper with its fixed height. The focused cards render their own shell.
  const fullPaneSx = {
    padding: theme.spacing(2),
    display: "flex",
    overflow: "auto",
    flexDirection: "column",
    height: cardHeight,
  };

  const renderCard = (id: string, path: DashboardPath, stackLength: number) => {
    const card = findCard(id);
    if (!card) return null;
    const body = card.bare ? (
      <Paper sx={fullPaneSx}>{card.render(cardContext)}</Paper>
    ) : (
      card.render(cardContext)
    );

    return (
      <Box
        key={`${id}-${path.column}-${path.item}-${path.card ?? 0}`}
        sx={
          editing
            ? {
                outline: `1px dashed ${theme.palette.divider}`,
                outlineOffset: 3,
                borderRadius: 1.5,
              }
            : undefined
        }
      >
        {editing && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 0.5,
            }}
          >
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {card.label}
            </Typography>
            <DashboardCardMenu
              path={path}
              stackLength={stackLength}
              columnCount={layout.columns.length}
              onMoveWithinStack={(target, offset) =>
                update(moveCardWithinStack(layout, target, offset))
              }
              onMoveToColumn={(target, column) =>
                update(moveCardToColumn(layout, target, column))
              }
              onMoveOutOfSplit={(target) =>
                update(moveCardOutOfSplit(layout, target))
              }
              onMoveAcrossSplit={(target) =>
                update(moveCardAcrossSplit(layout, target))
              }
              onRemove={(target) => update(removeCard(layout, target))}
            />
          </Box>
        )}
        {body}
      </Box>
    );
  };

  const renderItem = (
    item: DashboardItem,
    columnIndex: number,
    itemIndex: number,
    itemCount: number,
  ) => {
    if (!isSplitItem(item)) {
      return renderCard(
        item.id,
        { column: columnIndex, item: itemIndex },
        itemCount,
      );
    }

    const total = item.parts[0].width + item.parts[1].width;
    // Rounding each side independently can land on 11 or 13 (a 5:3 split of an
    // 8-wide column rounds 7.5 and 4.5 both up), which wraps the nested grid.
    // Round the first side only and give the second whatever is left.
    const firstSpan = Math.round((item.parts[0].width / total) * GRID_COLUMNS);
    const spans = [firstSpan, GRID_COLUMNS - firstSpan];
    return (
      <Box
        key={`split-${columnIndex}-${itemIndex}`}
        sx={{ display: "flex", flexDirection: "column", gap: 1 }}
      >
        {editing && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, pl: 0.5 }}>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {`Split ${item.parts[0].width} : ${item.parts[1].width}`}
            </Typography>
            <Box sx={{ ml: "auto" }}>
              <SplitMenu
                column={columnIndex}
                item={itemIndex}
                widths={[item.parts[0].width, item.parts[1].width]}
                onResize={(column, index, firstWidth) =>
                  update(resizeSplit(layout, column, index, firstWidth))
                }
                onRemove={(column, index) =>
                  update(removeSplit(layout, column, index))
                }
              />
            </Box>
          </Box>
        )}
        <Grid container spacing={2}>
          {item.parts.map((part, partIndex) => (
            <Grid
              item
              key={`part-${columnIndex}-${itemIndex}-${partIndex}`}
              xs={12}
              lg={spans[partIndex]}
            >
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {part.items.map((card, cardIndex) =>
                  renderCard(
                    card.id,
                    {
                      column: columnIndex,
                      item: itemIndex,
                      part: partIndex,
                      card: cardIndex,
                    },
                    part.items.length,
                  ),
                )}
                {editing && !part.items.length && (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 3,
                      textAlign: "center",
                      color: "text.secondary",
                      borderStyle: "dashed",
                    }}
                  >
                    <Typography variant="body2">
                      Empty — move a card here
                    </Typography>
                  </Paper>
                )}
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  };

  return (
    <Grid item xs={12}>
      <Box
        sx={{
          display: editing || editingProp === undefined ? "flex" : "none",
          alignItems: "center",
          gap: 1,
          mb: editing ? 1.5 : 1,
        }}
      >
        {editing && (
          <>
            <Button
              size="small"
              startIcon={<AddIcon />}
              disabled={!available.length}
              onClick={(event) => setAddAnchor(event.currentTarget)}
              sx={{ textTransform: "none" }}
              data-testid="dashboard-add-card"
            >
              Add card
            </Button>
            <Menu
              anchorEl={addAnchor}
              open={Boolean(addAnchor)}
              onClose={() => setAddAnchor(null)}
            >
              {available.map((card) => (
                <MenuItem
                  key={card.id}
                  onClick={() => {
                    update(addCard(layout, card.id));
                    setAddAnchor(null);
                  }}
                >
                  <ListItemText>{card.label}</ListItemText>
                </MenuItem>
              ))}
            </Menu>
            <Button
              size="small"
              disabled={layout.columns.length >= 4}
              onClick={() => update(addColumn(layout))}
              sx={{ textTransform: "none" }}
            >
              Add column
            </Button>
            {/* Two whole layouts to fall back to: the card set, or the two
                detail panes for anyone who would rather read the tables they
                already know. */}
            <Button
              size="small"
              disabled={!isCustomised}
              onClick={resetLayout}
              sx={{ textTransform: "none" }}
              data-testid="dashboard-reset"
            >
              Reset to cards
            </Button>
            <Button
              size="small"
              onClick={() => update(classicOrgDashboardLayout)}
              sx={{ textTransform: "none" }}
              data-testid="dashboard-reset-classic"
            >
              Reset to classic panes
            </Button>
          </>
        )}
        {(editingProp === undefined || editing) && (
          <Button
            size="small"
            onClick={() => setEditing(!editing)}
            sx={{ ml: "auto", textTransform: "none" }}
            data-testid="dashboard-toggle-edit"
          >
            {editing ? "Done" : "Customise layout"}
          </Button>
        )}
      </Box>

      <Grid container spacing={2} alignItems="flex-start">
        {layout.columns.map((column, columnIndex) => (
          <Grid
            item
            key={`column-${columnIndex}`}
            xs={12}
            lg={column.width}
            data-testid={`dashboard-column-${columnIndex}`}
          >
            {editing && (
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  {`Column ${columnIndex + 1} — ${column.width} of ${GRID_COLUMNS}`}
                </Typography>
                <Box sx={{ ml: "auto" }}>
                  <ColumnMenu
                    index={columnIndex}
                    width={column.width}
                    neighbourWidth={
                      layout.columns[
                        columnIndex < layout.columns.length - 1
                          ? columnIndex + 1
                          : Math.max(0, columnIndex - 1)
                      ]?.width ?? MIN_COLUMN_SPAN
                    }
                    columnCount={layout.columns.length}
                    onResize={(index, width) =>
                      update(resizeColumn(layout, index, width))
                    }
                    onSplit={(index) => update(splitColumn(layout, index))}
                    onAddColumn={() => update(addColumn(layout))}
                    onMoveColumn={(index, offset) =>
                      update(moveColumn(layout, index, offset))
                    }
                    onRemoveColumn={(index) =>
                      update(removeColumn(layout, index))
                    }
                  />
                </Box>
              </Box>
            )}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {column.items.map((item, itemIndex) =>
                renderItem(item, columnIndex, itemIndex, column.items.length),
              )}
              {editing && !column.items.length && (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 4,
                    textAlign: "center",
                    color: "text.secondary",
                    borderStyle: "dashed",
                  }}
                >
                  <Typography variant="body2">
                    Empty column — add a card or move one here
                  </Typography>
                </Paper>
              )}
            </Box>
          </Grid>
        ))}
      </Grid>
    </Grid>
  );
};

export default OrgDashboard;
